/**
 * Drafts Composable
 * Session-only localStorage draft saving that clears on tab close
 */

const DRAFT_KEY_PREFIX = 'feedbear_draft_';

/**
 * Create a draft manager for an editor
 * @param {string} editorId - Unique identifier for the editor instance
 * @param {Object} options - Configuration options
 * @returns {Object} Draft management methods
 */
export function useDrafts(editorId, options = {}) {
  const draftKey = `${DRAFT_KEY_PREFIX}${editorId}`;
  const {
    debounceMs = 1000,
    useSessionStorage = true
  } = options;

  // Use sessionStorage by default (clears on tab close)
  // Use localStorage if persistence across sessions is needed
  const storage = useSessionStorage ? sessionStorage : localStorage;

  let debounceTimer = null;

  /**
   * Save draft content
   * @param {string} content - The content to save
   */
  function saveDraft(content) {
    if (content && content.trim()) {
      try {
        storage.setItem(draftKey, JSON.stringify({
          content,
          savedAt: Date.now()
        }));
      } catch (e) {
        // Storage might be full or disabled
        console.warn('Failed to save draft:', e);
      }
    } else {
      clearDraft();
    }
  }

  /**
   * Save draft with debouncing
   * @param {string} content - The content to save
   */
  function saveDraftDebounced(content) {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    debounceTimer = setTimeout(() => {
      saveDraft(content);
    }, debounceMs);
  }

  /**
   * Load saved draft
   * @returns {string|null} The saved content or null
   */
  function loadDraft() {
    try {
      const data = storage.getItem(draftKey);
      if (data) {
        const parsed = JSON.parse(data);
        return parsed.content || null;
      }
    } catch (e) {
      console.warn('Failed to load draft:', e);
    }
    return null;
  }

  /**
   * Get draft metadata
   * @returns {Object|null} Draft metadata or null
   */
  function getDraftInfo() {
    try {
      const data = storage.getItem(draftKey);
      if (data) {
        const parsed = JSON.parse(data);
        return {
          content: parsed.content,
          savedAt: parsed.savedAt ? new Date(parsed.savedAt) : null,
          exists: true
        };
      }
    } catch (e) {
      console.warn('Failed to get draft info:', e);
    }
    return { exists: false };
  }

  /**
   * Clear the saved draft
   */
  function clearDraft() {
    try {
      storage.removeItem(draftKey);
    } catch (e) {
      console.warn('Failed to clear draft:', e);
    }

    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }

  /**
   * Check if a draft exists
   * @returns {boolean} True if draft exists
   */
  function hasDraft() {
    try {
      return storage.getItem(draftKey) !== null;
    } catch (e) {
      return false;
    }
  }

  /**
   * Cleanup function to cancel pending saves
   */
  function cleanup() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }

  return {
    saveDraft,
    saveDraftDebounced,
    loadDraft,
    getDraftInfo,
    clearDraft,
    hasDraft,
    cleanup
  };
}

/**
 * Clear all FeedBear drafts from storage
 * @param {boolean} useSessionStorage - Whether to clear sessionStorage (true) or localStorage (false)
 */
export function clearAllDrafts(useSessionStorage = true) {
  const storage = useSessionStorage ? sessionStorage : localStorage;

  try {
    const keysToRemove = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(DRAFT_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => storage.removeItem(key));
  } catch (e) {
    console.warn('Failed to clear all drafts:', e);
  }
}

/**
 * Generate a unique editor ID based on context
 * @param {string} type - The editor type (e.g., 'comment', 'post', 'changelog')
 * @param {string|number} contextId - Context identifier (e.g., post ID, board ID)
 * @returns {string} Unique editor ID
 */
export function generateEditorId(type, contextId) {
  return `${type}_${contextId || 'new'}`;
}

export default useDrafts;
